/**
 * DecisionRecordView — one memorialized decision, rendered in full.
 *
 * The one renderer for a decision's record. Two pages mount it: the
 * canonical page at /prioritization/decisions/:decisionId (the product's
 * own shell, like an item's page) and the read-only Decisions app at
 * /apps/decisions/:decisionId. Until 2026-10-07 the canonical URL
 * redirected to the decision log's map with the record squeezed into its
 * side panel, and the only full page lived in the app.
 *
 * Renders what was recorded and says so honestly when something wasn't.
 */

import { Link } from 'react-router-dom';
import {
  DECISION_CLASS_LABELS,
  DECISION_SUBJECT_KIND_LABELS,
  decisionRelatedObjectHref,
  resolveSubjectKind,
  type Decision,
  type DecisionRelatedObject,
} from '@/types/prioritization/decisions';
import { AppCard, Eyebrow, StatusChip } from '@/apps/components/primitives';
import { formatDecisionDate } from './decisionRecordText';
import { decisionRelatedKindLabel, useDecisionRelatedNames } from './useDecisionRelatedNames';
import { DecisionPlanOfRecordLink } from './DecisionPlanOfRecordLink';
import { CallSentence } from './CallSentence';

/** Canonical deep link for a related object, where a stable route exists. */
function relatedObjectHref(ref: DecisionRelatedObject): string | null {
  switch (ref.kind) {
    case 'item':
      return `/prioritization/items/${ref.id}?tab=lineage`;
    case 'cycle':
      return `/prioritization/cycles/${ref.id}/report`;
    case 'track':
      return `/prioritization/tracks/${ref.id}`;
    case 'session':
      return `/prioritization/sessions/${ref.id}`;
    default:
      // Everything else opens where the decision log opens it — a plan of
      // record's own decision links to its plan this way.
      return decisionRelatedObjectHref(ref);
  }
}

/** Reached through their plan, so they are not shown as chips of their own. */
const PLAN_INTERNAL_KINDS: ReadonlySet<string> = new Set([
  'plan_version',
  'plan_of_record',
  'plan_of_record_snapshot',
]);

function RationaleBlock({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div>
      <Eyebrow>{label}</Eyebrow>
      <p className="mt-1 whitespace-pre-wrap font-body text-sm text-[var(--text-sec)]">
        {value}
      </p>
    </div>
  );
}

export interface DecisionRecordViewProps {
  decision: Decision;
  /** Every decision on the record, for the lineage links' titles. */
  decisions: readonly Decision[];
  /** The URL of another decision's page, for lineage links. */
  decisionHref: (id: string) => string;
}

export function DecisionRecordView({ decision, decisions, decisionHref }: DecisionRecordViewProps) {
  const subjectKind = resolveSubjectKind(decision);
  const hasStructuredRationale =
    decision.rationaleContext ||
    decision.rationaleReasoning ||
    decision.rationaleAlternatives ||
    decision.reconsiderationConditions;

  /** Decisions elsewhere in the record that replaced this one. */
  const supersededBy = decisions.filter((d) => d.supersedesDecisionId === decision.id);

  // Related objects: merge the legacy single back-links with multi-attach,
  // deduped by kind+id.
  const related: DecisionRelatedObject[] = [];
  const seen = new Set<string>();
  const push = (raw: DecisionRelatedObject) => {
    // The database writes some of these itself, and the cut-line decisions
    // spell the kind as `type`. Read either; skip an entry with no kind or id
    // rather than fail the whole page on it.
    const kind = raw.kind ?? (raw as unknown as { type?: DecisionRelatedObject['kind'] }).type;
    if (typeof kind !== 'string' || typeof raw.id !== 'string') return;
    const ref: DecisionRelatedObject = { kind, id: raw.id };
    const key = `${ref.kind}:${ref.id}`;
    if (!seen.has(key)) {
      seen.add(key);
      related.push(ref);
    }
  };
  if (decision.itemId) push({ kind: 'item', id: decision.itemId });
  if (decision.sessionId) push({ kind: 'session', id: decision.sessionId });
  if (decision.cycleId) push({ kind: 'cycle', id: decision.cycleId });
  decision.relatedObjects.forEach(push);
  const { nameFor } = useDecisionRelatedNames(related);
  // The plan is not a chip: it gets its own block, by name, above the rest.
  const planCycleId = related.find((r) => r.kind === 'plan_cycle')?.id ?? null;
  const shownRelated = related.filter(
    (r) => r.kind !== 'plan_cycle' && !PLAN_INTERNAL_KINDS.has(r.kind),
  );

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        <StatusChip
          label={decision.decisionKind === 'auto' ? 'System-recorded' : 'Manual'}
          tone="slate"
        />
        {decision.decisionClass ? (
          <StatusChip label={DECISION_CLASS_LABELS[decision.decisionClass]} tone="neutral" />
        ) : null}
        {subjectKind ? (
          <StatusChip label={DECISION_SUBJECT_KIND_LABELS[subjectKind]} tone="neutral" />
        ) : null}
        {decision.impact === 'high' ? <StatusChip label="High impact" tone="mustard" /> : null}
        {decision.status === 'superseded' ? <StatusChip label="Superseded" tone="neutral" /> : null}
        {decision.status === 'reversed' ? <StatusChip label="Reversed" tone="coral" /> : null}
      </div>

      <div className="space-y-3">
        <CallSentence decision={decision} decisions={decisions} relatedNames={shownRelated.map((r) => nameFor(r)).filter((n): n is string => typeof n === 'string' && n.length > 0)} />

        <AppCard>
          <Eyebrow>Decided</Eyebrow>
          <p className="mt-1 font-body text-sm text-[var(--text-pri)]">
            {decision.decidedByName ?? (decision.decisionKind === 'auto' ? 'The system, from a governance event' : 'Unattributed')}
            {' · '}
            {formatDecisionDate(decision.decidedAt)}
            {decision.site ? ` · ${decision.site}` : ''}
          </p>
          {decision.body ? (
            <p className="mt-3 whitespace-pre-wrap font-body text-sm text-[var(--text-sec)]">
              {decision.body}
            </p>
          ) : null}
        </AppCard>

        {/* The form saves a typed rationale as the decision's body, shown
            above. "Why" is for the structured fields and the review date; it
            says nothing was recorded only when nothing was. */}
        {hasStructuredRationale || decision.reviewByDate || !decision.body?.trim() ? (
        <AppCard>
          <p className="mb-3 font-heading font-semibold text-[var(--text-pri)]">
            Why
          </p>
          {hasStructuredRationale ? (
            <div className="space-y-3">
              <RationaleBlock label="Context at the time" value={decision.rationaleContext} />
              <RationaleBlock label="Reasoning" value={decision.rationaleReasoning} />
              <RationaleBlock label="Alternatives considered" value={decision.rationaleAlternatives} />
              <RationaleBlock label="Revisit when" value={decision.reconsiderationConditions} />
            </div>
          ) : !decision.body?.trim() ? (
            <p className="font-body text-sm text-[var(--text-sec)]">
              No rationale was recorded for this decision.
            </p>
          ) : null}
          {decision.reviewByDate ? (
            <p className="mt-3 font-mono text-[11px] uppercase tracking-wider text-[var(--text-ter)]">
              Review by {formatDecisionDate(decision.reviewByDate)}
            </p>
          ) : null}
        </AppCard>
        ) : null}

        {planCycleId ? <DecisionPlanOfRecordLink planCycleId={planCycleId} /> : null}

        {shownRelated.length > 0 ? (
          <AppCard>
            <p className="mb-2 font-heading font-semibold text-[var(--text-pri)]">
              On the record with
            </p>
            <div className="flex flex-wrap gap-1.5">
              {shownRelated.map((ref) => {
                const href = relatedObjectHref(ref);
                // A name, never an identifier: the object's own name with its
                // kind in front, or the kind alone while the name is unknown.
                const kindLabel = decisionRelatedKindLabel(ref.kind);
                const name = nameFor(ref);
                const label = name ? `${kindLabel}: ${name}` : kindLabel;
                return href ? (
                  <Link
                    key={`${ref.kind}:${ref.id}`}
                    to={href}
                    className="rounded-full border border-[var(--rule)] px-2.5 py-1 font-body text-xs text-[var(--text-pri)] hover:bg-[var(--hover-tint)]"
                  >
                    {label}
                  </Link>
                ) : (
                  <span
                    key={`${ref.kind}:${ref.id}`}
                    className="rounded-full border border-[var(--rule)] px-2.5 py-1 font-body text-xs text-[var(--text-sec)]"
                  >
                    {label}
                  </span>
                );
              })}
            </div>
            {decision.itemId ? (
              <p className="mt-3 font-body text-xs text-[var(--text-ter)]">
                <Link
                  to={`/prioritization/items/${decision.itemId}?tab=lineage&why=${decision.itemId}`}
                  className="text-[var(--text-sec)] underline-offset-2 hover:underline"
                >
                  Open the full Decision Trail for this item
                </Link>
              </p>
            ) : null}
          </AppCard>
        ) : null}

        {decision.parentDecisionId || decision.supersedesDecisionId || supersededBy.length > 0 ? (
          <AppCard>
            <p className="mb-2 font-heading font-semibold text-[var(--text-pri)]">
              Decision lineage
            </p>
            <div className="space-y-1.5 font-body text-sm">
              {decision.parentDecisionId ? (
                <p>
                  <span className="text-[var(--text-sec)]">Part of </span>
                  <Link
                    to={decisionHref(decision.parentDecisionId)}
                    className="text-[var(--text-pri)] underline-offset-2 hover:underline"
                  >
                    {decisions.find((d) => d.id === decision.parentDecisionId)?.title ?? 'a larger decision'}
                  </Link>
                </p>
              ) : null}
              {decision.supersedesDecisionId ? (
                <p>
                  <span className="text-[var(--text-sec)]">Supersedes </span>
                  <Link
                    to={decisionHref(decision.supersedesDecisionId)}
                    className="text-[var(--text-pri)] underline-offset-2 hover:underline"
                  >
                    {decisions.find((d) => d.id === decision.supersedesDecisionId)?.title ?? 'an earlier decision'}
                  </Link>
                </p>
              ) : null}
              {supersededBy.map((d) => (
                <p key={d.id}>
                  <span className="text-[var(--text-sec)]">Superseded by </span>
                  <Link
                    to={decisionHref(d.id)}
                    className="text-[var(--text-pri)] underline-offset-2 hover:underline"
                  >
                    {d.title}
                  </Link>
                </p>
              ))}
            </div>
          </AppCard>
        ) : null}
      </div>
    </>
  );
}
